/**
 * The optional post-game survey (design doc §20.2), about 30 seconds: five 1–5 ratings, one
 * free-text question and the player's experience. It is shown on the End screen, after the
 * reveal, only when the host chose to share game data (the `room` message). Every answer is
 * optional; the player can skip it; the server keeps one submission per player.
 */
import {
  GENRE_EXPERIENCE,
  SURVEY_FREE_TEXT_QUESTION,
  SURVEY_RATING_KEYS,
  SURVEY_RATING_QUESTIONS,
  SURVEY_TEXT_MAX,
  type GenreExperience,
  type SurveyRatingKey,
} from "@handshake/protocol/constants";
import { useState } from "react";
import { send, useGame } from "../lib/store.ts";

const GENRE_LABELS: Readonly<Record<GenreExperience, string>> = {
  new: "New to them",
  some: "Played a few",
  lots: "Played lots",
};

type Ratings = Record<SurveyRatingKey, number | null>;

const NO_RATINGS = Object.fromEntries(SURVEY_RATING_KEYS.map((k) => [k, null])) as Ratings;

export function Survey() {
  const room = useGame((s) => s.room);
  const [open, setOpen] = useState(true);
  const [ratings, setRatings] = useState<Ratings>(NO_RATINGS);
  const [text, setText] = useState("");
  const [firstGame, setFirstGame] = useState<boolean | null>(null);
  const [genre, setGenre] = useState<GenreExperience | null>(null);
  const [sending, setSending] = useState(false);

  if (!room?.analytics) return null;
  if (room.surveySubmitted) {
    return (
      <section className="panel center" data-testid="survey-done">
        <p style={{ margin: 0 }}>Thank you for answering the survey.</p>
      </section>
    );
  }
  if (!open) {
    return (
      <section className="panel row" data-testid="survey-skipped">
        <span className="grow muted">Survey skipped.</span>
        <button type="button" className="btn small ghost" onClick={() => setOpen(true)}>
          Answer it after all
        </button>
      </section>
    );
  }

  const answered =
    Object.values(ratings).some((v) => v !== null) ||
    text.trim().length > 0 ||
    firstGame !== null ||
    genre !== null;

  const submit = async () => {
    setSending(true);
    await send({
      type: "submitSurvey",
      survey: {
        ...ratings,
        freeText: text.trim().length > 0 ? text.trim() : null,
        firstGame,
        genreExperience: genre,
      },
    });
    setSending(false);
  };

  return (
    <section className="panel stack" data-testid="survey">
      <div>
        <div className="kicker">Quick survey · about 30 seconds</div>
        <h3 style={{ margin: "0.25rem 0 0" }}>How was it?</h3>
        <p className="faint" style={{ margin: "0.25rem 0 0" }}>
          Optional and anonymous: answers are stored under a pseudonym with this game's data. 1 =
          not at all, 5 = very much.
        </p>
      </div>
      {SURVEY_RATING_KEYS.map((key) => (
        <RatingRow
          key={key}
          id={key}
          question={SURVEY_RATING_QUESTIONS[key]}
          value={ratings[key]}
          onChange={(v) => setRatings((r) => ({ ...r, [key]: v }))}
        />
      ))}
      <label className="field">
        <span>{SURVEY_FREE_TEXT_QUESTION}</span>
        <textarea
          className="text"
          data-testid="survey-text"
          maxLength={SURVEY_TEXT_MAX}
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ minHeight: "5rem" }}
        />
      </label>
      <Choice
        label="Is this your first game of HANDSHAKE?"
        testId="survey-first-game"
        options={[
          { value: true, label: "First game" },
          { value: false, label: "Played before" },
        ]}
        value={firstGame}
        onChange={setFirstGame}
      />
      <Choice
        label="Social deduction games in general?"
        testId="survey-genre"
        options={GENRE_EXPERIENCE.map((g) => ({ value: g, label: GENRE_LABELS[g] }))}
        value={genre}
        onChange={setGenre}
      />
      <div className="row">
        <button
          type="button"
          className="btn ghost"
          data-testid="survey-skip"
          onClick={() => setOpen(false)}
        >
          Skip
        </button>
        <button
          type="button"
          className="btn primary grow"
          data-testid="survey-submit"
          disabled={!answered || sending}
          onClick={() => void submit()}
        >
          Send
        </button>
      </div>
    </section>
  );
}

function RatingRow({
  id,
  question,
  value,
  onChange,
}: {
  id: string;
  question: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <div id={`survey-q-${id}`}>{question}</div>
      <div
        className="survey-scale"
        role="radiogroup"
        aria-labelledby={`survey-q-${id}`}
        data-testid={`survey-${id}`}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            className={value === n ? "btn small primary" : "btn small"}
            onClick={() => onChange(value === n ? null : n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function Choice<T extends string | boolean>({
  label,
  testId,
  options,
  value,
  onChange,
}: {
  label: string;
  testId: string;
  options: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (v: T | null) => void;
}) {
  return (
    <div>
      <div>{label}</div>
      <div className="survey-scale" role="radiogroup" aria-label={label} data-testid={testId}>
        {options.map((o) => (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={value === o.value ? "btn small primary" : "btn small"}
            onClick={() => onChange(value === o.value ? null : o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
